import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const statusConfig = {
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'in-progress': { label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  'findings-documented': { label: 'Findings Documented', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400' },
  ongoing: { label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  initiated: { label: 'Initiated', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
};

const severityConfig = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  major: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  moderate: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  minor: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
};

const WorkspaceHeader = ({ investigation, onSubmitForApproval, onApprove, onReject, currentRole }) => {
  const navigate = useNavigate();
  const status = statusConfig?.[investigation?.status] || statusConfig?.['in-progress'];
  const severity = severityConfig?.[investigation?.severity] || severityConfig?.moderate;

  const canSubmit = ['in-progress', 'ongoing', 'findings-documented']?.includes(investigation?.status) && currentRole === 'lead';
  const canApprove = investigation?.status === 'findings-documented' && currentRole === 'approver';

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6 shadow-sm">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/incident-investigation-tracker')}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="ArrowLeft" size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-sm font-bold text-primary">{investigation?.incidentId}</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${status?.color}`}>
                {status?.label}
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${severity}`}>
                {investigation?.severity?.toUpperCase()}
              </span>
            </div>
            <h1 className="text-xl font-bold text-foreground leading-tight">{investigation?.title}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Icon name="User" size={13} />
                {investigation?.investigator}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="MapPin" size={13} />
                {investigation?.location?.replace(/-/g, ' ')?.toUpperCase()}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Calendar" size={13} />
                Incident: {investigation?.incidentDate ? format(new Date(investigation.incidentDate), 'MMM dd, yyyy') : '—'}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={13} />
                Target: {investigation?.targetCompletionDate ? format(new Date(investigation.targetCompletionDate), 'MMM dd, yyyy') : '—'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {canSubmit && (
            <Button variant="default" iconName="Send" onClick={onSubmitForApproval}>
              Submit for Approval
            </Button>
          )}
          {canApprove && (
            <>
              <Button variant="outline" iconName="XCircle" onClick={onReject} className="text-red-600 border-red-300 hover:bg-red-50">
                Reject
              </Button>
              <Button variant="default" iconName="CheckCircle" onClick={onApprove} className="bg-emerald-600 hover:bg-emerald-700">
                Approve Investigation
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">WORKFLOW PROGRESS</span>
        </div>
        <div className="flex items-center gap-0 mt-2 overflow-x-auto pb-1">
          {['Assigned', 'In Progress', 'Findings Documented', 'Approved', 'Closed']?.map((step, i) => {
            const stepStatuses = [['assigned', 'initiated'], ['in-progress', 'ongoing'], ['findings-documented'], ['approved'], ['closed', 'completed']];
            const isActive = stepStatuses?.[i]?.includes(investigation?.status);
            const isPast = stepStatuses?.slice(0, i)?.some(s => s?.includes(investigation?.status));
            return (
              <React.Fragment key={step}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'bg-primary text-primary-foreground' :
                  isPast ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400': 'bg-muted text-muted-foreground'
                }`}>
                  {isPast && !isActive && <Icon name="Check" size={11} />}
                  {step}
                </div>
                {i < 4 && <Icon name="ChevronRight" size={14} className="text-muted-foreground mx-0.5 flex-shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceHeader;
